// src/router/authGuard.ts
import {
  actingTenantFor,
  effectiveAccessQueryOptions,
  getLoginSession,
} from "@/api/access/access.queries";
import type { EffectiveAccess, SwitchRef } from "@/api/access/access.types";
import type { RouterContextType } from "@/api/permissions/permissions.api.ts";
import {
  permissionsQueryOptions,
  resolveModuleFromPath,
  resolveSwitchFromPath,
} from "@/api/permissions/permissions.api.ts";
import type { PermissionsResponse } from "@/api/permissions/permissions.types";
import { myEntitiesQueryOptions } from "@/api/entities/entities.queries";
import type { MyEntity } from "@/api/entities/entities.queries.types";
import { SERVICE_PROVIDER, SUPER_ADMIN } from "@/components/data/constants";
import {
  BARE_BONES_HIDDEN_ROUTES,
  ENTITY_ACCESS_ROUTE_KEYS,
  isEntityHiddenRoute,
  isEntityLevelRoute,
  isMergedViewHiddenRoute,
  MAIN_APP_HIDDEN_ROUTES,
} from "@/constants/routesConfig";
import { getModuleAccess } from "@/hooks/auth/usePermissions";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { getDestinationScope } from "@/hooks/useRequestScope";
import { SERVENGINE_SCOPE, useEntityTenantStore } from "@/store/entityTenantStore";
import { useRecordTenantStore } from "@/store/recordTenantStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { useSpInviteSessionStore } from "@/store/useSpInviteSessionStore";
import { hasSwitch, hasSwitchAnywhere, scopeTenantId } from "@/utils/access";
import { requireAuth } from "@/utils/authHelpers";
import { isAppLevelUser, isInsideEntity } from "@/utils/isPlatformView";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { isAxiosError } from "axios";

const SP_STATUS_TARGETS: Record<string, string> = {
  ready_for_review: "/sp-invite/review/complete-submission",
  "in-review": "/sp-invite/review/alignment-scheduled",
  alignment: "/sp-invite/review/alignment-scheduled",
};

const SP_SPLASH_ELIGIBLE_STATUSES = new Set(["invited", "in-progress"]);

const SP_HOME_PATH = "/sp-home";
const SP_REACHABLE_PATH_PREFIXES = [SP_HOME_PATH, "/mobile/chat"];

/**
 * SERV-3232: a pasted record link names the record's entity in ?tenant=, and the backend refuses
 * the permissions read when she is not in that entity. That refusal is the answer, so it is not
 * retried, and she is sent home in the scope she already had instead of to an error screen.
 */
const isRecordEntityRefusal = (error: unknown, search: Record<string, unknown> | undefined) =>
  isAxiosError(error) &&
  error.response?.status === 403 &&
  (error.response.data?.error?.code === "INVALID_ACTING_ENTITY" ||
    (typeof search?.tenant === "string" && search.tenant !== ""));

const retryUnlessRefused = (
  queryClient: QueryClient,
  search: Record<string, unknown> | undefined,
) => {
  const defaultRetry = queryClient.getDefaultOptions().queries?.retry ?? 3;
  return (failureCount: number, error: Error): boolean => {
    if (isRecordEntityRefusal(error, search)) return false;
    if (typeof defaultRetry === "function") return defaultRetry(failureCount, error);
    if (typeof defaultRetry === "number") return failureCount < defaultRetry;
    return defaultRetry;
  };
};

type BeforeLoadArgs = {
  context: RouterContextType;
  location: {
    pathname: string;
    search?: Record<string, unknown>;
    href?: string;
  };
};

/**
 * Contract 7.4: a page the catalog names opens on the engine's switch, at the scoped company or,
 * in a merged view, at any company. Null when the effective access cannot be read: the route then
 * keeps the module check rather than locking everyone out, since the server check is the
 * protection and hiding never is.
 */
async function holdsRouteSwitch(
  queryClient: QueryClient,
  scope: string,
  routeSwitch: SwitchRef,
): Promise<boolean | null> {
  let access: EffectiveAccess;
  try {
    access = await queryClient.ensureQueryData({
      ...effectiveAccessQueryOptions(scope),
      retry: false,
    });
  } catch {
    return null;
  }
  const company = scopeTenantId(actingTenantFor(scope, getLoginSession()), access);
  return company ? hasSwitch(access, company, routeSwitch) : hasSwitchAnywhere(access, routeSwitch);
}

/** Null when the path has no switch entry or the answer cannot be read: the module check decides. */
async function routeSwitchDecision(
  queryClient: QueryClient,
  location: BeforeLoadArgs["location"],
): Promise<boolean | null> {
  const routeSwitch = resolveSwitchFromPath(location.pathname);
  if (!routeSwitch) return null;
  return holdsRouteSwitch(queryClient, getDestinationScope(location.search), routeSwitch);
}

export async function authGuardWithPermissions({ context, location }: BeforeLoadArgs) {
  await requireAuth(location);

  const { userId, userType, isMainAppLogin, isEntityLogin, isAppLevel, allEntities } =
    getActiveSessionState();

  // SERV-3097: for the MVP a provider does not sign in on the web (the backend
  // refuses the login and every call a provider session makes without the mobile
  // app's headers), so such a session gets one page that offers to log out. The
  // exception is /mobile/chat, which the mobile app opens in a webview as its
  // Chat tab. This runs ahead of the onboarding redirects below, which would
  // otherwise bounce between /sp-invite and here. The provider screens are left
  // in place for when provider sign-in returns. An admin impersonating a provider
  // is not refused by the backend, so they keep the screens and the onboarding
  // redirects.
  if (userType === SERVICE_PROVIDER && !useImpersonationStore.getState().isImpersonating) {
    if (!SP_REACHABLE_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) {
      throw redirect({ to: SP_HOME_PATH });
    }
  } else if (userType === SERVICE_PROVIDER) {
    const splashData = useSpInviteSessionStore.getState().splashData;
    const status = splashData?.onboardingStatus?.value;

    if (
      splashData?.shouldShowSplash &&
      status &&
      SP_SPLASH_ELIGIBLE_STATUSES.has(status) &&
      !location.pathname.startsWith("/sp-invite")
    ) {
      throw redirect({ to: "/sp-invite" });
    }

    const target = status ? SP_STATUS_TARGETS[status] : undefined;
    if (target && !location.pathname.startsWith(target)) {
      throw redirect({ to: target });
    }
  }

  const segments = location.pathname.split("/").filter(Boolean);
  const basePath = segments.length > 0 ? `/${segments[0]}` : location.pathname;

  // Ahead of the super-admin and impersonation returns below, either of which
  // would otherwise wave an entity session straight through.
  const entityTenant = useEntityTenantStore.getState().entityTenant;
  let insideEntity = isInsideEntity({
    isEntityLogin,
    entityTenant,
    isImpersonating: useImpersonationStore.getState().isImpersonating,
  });
  const appLevelUser = isAppLevelUser({
    isAppLevel,
    allEntities: allEntities ?? false,
    userType,
  });

  let pickedEntity: MyEntity | undefined;
  if (appLevelUser && !isEntityLogin && insideEntity) {
    await context.queryClient.ensureQueryData(myEntitiesQueryOptions(userId));
    const myEntities = context.queryClient.getQueryData<MyEntity[]>(
      myEntitiesQueryOptions(userId).queryKey,
    );
    pickedEntity = myEntities?.find((entity) => entity.id === entityTenant);
    // A scope persisted from an entity she has since left. Redirecting to Home
    // would loop on Home itself, so drop to the merged view and judge the route
    // there.
    if (!pickedEntity) {
      useEntityTenantStore.getState().setEntityTenant(SERVENGINE_SCOPE);
      insideEntity = false;
    }
  }

  if (isMainAppLogin && !(appLevelUser && insideEntity) && MAIN_APP_HIDDEN_ROUTES.has(basePath)) {
    throw redirect({ to: "/" });
  }

  if (BARE_BONES_HIDDEN_ROUTES.has(basePath)) {
    throw redirect({ to: "/" });
  }

  if (insideEntity && isEntityHiddenRoute(location.pathname)) {
    throw redirect({ to: "/" });
  }

  if (appLevelUser && !insideEntity && isMergedViewHiddenRoute(location.pathname)) {
    throw redirect({ to: "/" });
  }

  if (appLevelUser && !insideEntity && isEntityLevelRoute(location.pathname)) {
    throw redirect({ to: "/" });
  }

  if (userType === SUPER_ADMIN) {
    if (isEntityLogin) {
      if (basePath !== "/" && !ENTITY_ACCESS_ROUTE_KEYS.has(basePath)) {
        throw redirect({ to: "/" });
      }
    }
    return;
  }

  const { isImpersonating, modules: impersonationModules } = useImpersonationStore.getState();

  if (isImpersonating) {
    const moduleKey = resolveModuleFromPath(location.pathname);
    if (!moduleKey) return;
    const allowed = new Set(impersonationModules ?? []);
    if (!allowed.has(moduleKey)) {
      throw redirect({ to: "/" });
    }
    return;
  }

  // SERV-3232: the permissions cache is the union across her entities; inside
  // one entity the route follows the role she holds there, as the sidebar does.
  if (pickedEntity) {
    const holds = await routeSwitchDecision(context.queryClient, location);
    if (holds === false) throw redirect({ to: "/" });
    if (holds === true) return;
    const scopedModuleKey = resolveModuleFromPath(location.pathname);
    if (!scopedModuleKey) return;
    if (!pickedEntity.modules.includes(scopedModuleKey)) {
      throw redirect({ to: "/" });
    }
    return;
  }

  // Prefetch permissions into TanStack Query cache
  const scopedPermissionsQueryOptions = permissionsQueryOptions(
    getDestinationScope(location.search),
  );
  try {
    await context.queryClient.ensureQueryData({
      ...scopedPermissionsQueryOptions,
      retry: retryUnlessRefused(context.queryClient, location.search),
    });
  } catch (error) {
    if (!isRecordEntityRefusal(error, location.search)) throw error;
    useRecordTenantStore.getState().clearRecordTenant();
    throw redirect({ to: "/" });
  }
  const permissions = context.queryClient.getQueryData<PermissionsResponse>(
    scopedPermissionsQueryOptions.queryKey,
  );

  const holds = await routeSwitchDecision(context.queryClient, location);
  if (holds === false) throw redirect({ to: "/" });
  if (holds === true) return;

  // resolve which module this path belongs to
  const moduleKey = resolveModuleFromPath(location.pathname);

  // If path is not tied to a protected module, allow access
  if (!moduleKey) return;

  const access = getModuleAccess(permissions, moduleKey);

  // If user does not have view access to the module, redirect to home
  if (!access.view) {
    throw redirect({ to: "/" });
  }
}
